import type React from "react";
import { twJoin } from "tailwind-merge";

import { NoData } from "@/components/NoData/NoData";
import { Inline } from "@/design-system/Inline";
import { isDefined } from "@/helpers/util";
import { useTranslation } from "@/translations";
import { Bar, BarChart, LabelList, Tooltip, XAxis, YAxis } from "@/ui-adapters/Recharts";
import { RuntimeHeight } from "@/ui-adapters/RuntimeHeight";

import { ChartTooltip } from "./ChartTooltip";

export type HistogramChartDataPoint<TCategory extends string = string> = {
  category: TCategory;
  label?: string;
  value: number;
};

type HistogramChartProps<TCategory extends string> = {
  data: HistogramChartDataPoint<TCategory>[];
  onClickBar?: (dataPoint: HistogramChartDataPoint<TCategory>) => void;
};

export function HistogramChart<TCategory extends string>({
  data,
  onClickBar,
}: HistogramChartProps<TCategory>): React.ReactNode {
  const { t } = useTranslation();
  const amountTotalDataPoints = data.reduce((sum, point) => sum + point.value, 0);

  if (amountTotalDataPoints === 0) {
    return <NoData />;
  }

  return (
    <RuntimeHeight height={data.length * 49} minHeight={80}>
      {(heightAttributes) => (
        <BarChart
          margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
          {...heightAttributes}
          layout="vertical"
          data={data}
          className="-ml-space-xs w-full"
          responsive
        >
          <XAxis type="number" dataKey="value" domain={[0, amountTotalDataPoints]} hide />
          <YAxis type="category" dataKey="category" hide />
          <Bar
            dataKey="value"
            barSize={8}
            maxBarSize={8}
            radius={2}
            // Recharts will skip rendering if value is 0
            minPointSize={Number.MIN_VALUE}
            className={twJoin("fill-background-brand-secondary", onClickBar && "cursor-pointer")}
            isAnimationActive={false}
            background={<BarBackground />}
            onClick={
              onClickBar
                ? (barData) => {
                    if ("category" in barData && "value" in barData) {
                      onClickBar(resolveDataPoint(data, barData.category, barData.value));
                    }
                  }
                : undefined
            }
          >
            <LabelList
              dataKey="category"
              content={({ x, y, height, value: category }) => {
                if (
                  !isDefined(x) ||
                  !isDefined(y) ||
                  !isDefined(height) ||
                  !isDefined(category) ||
                  typeof x !== "number" ||
                  typeof y !== "number" ||
                  typeof height !== "number"
                ) {
                  return null;
                }

                const dataPoint = resolveDataPoint(data, category, 0);
                const currValue = dataPoint.value;
                const label = dataPoint.label ?? String(category);
                const currPortionInPercentage =
                  amountTotalDataPoints === 0 ? "0" : ((currValue / amountTotalDataPoints) * 100).toFixed(0);
                const handleFilter = () => {
                  onClickBar?.(dataPoint);
                };

                return (
                  <g
                    role={onClickBar ? "button" : undefined}
                    tabIndex={onClickBar ? 0 : undefined}
                    aria-label={
                      onClickBar
                        ? t("component.chart.histogram.accessible-bar-label", { label, value: currValue })
                        : undefined
                    }
                    className={twJoin(
                      onClickBar &&
                        "cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand",
                    )}
                    onClick={
                      onClickBar
                        ? (event) => {
                            event.stopPropagation();
                            handleFilter();
                          }
                        : undefined
                    }
                    onKeyDown={
                      onClickBar
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleFilter();
                            }
                          }
                        : undefined
                    }
                    ref={(g) => {
                      if (!g) {
                        return;
                      }
                      const svg = g.ownerSVGElement;
                      if (!svg) {
                        return;
                      }

                      const elCategoryLabel = g.children.item(0) as SVGTextElement | null;
                      const elPercentageLabel = g.children.item(1) as SVGTextElement | null;
                      const elValueLabel = g.children.item(2) as SVGTextElement | null;

                      if (!elCategoryLabel || !elPercentageLabel || !elValueLabel) {
                        return;
                      }

                      const widthChart = svg.getBoundingClientRect().width;
                      const widthPercentageLabel = elPercentageLabel.getBBox().width;
                      const widthValueLabel = elValueLabel.getBBox().width;
                      const gap = 4;
                      const maxWidthCategoryLabel = Math.max(
                        0,
                        widthChart - x - widthValueLabel - gap * 3 - widthPercentageLabel,
                      );

                      elValueLabel.setAttribute("x", String(widthChart));

                      // Use binary search to dynamically find the most suitable label length for truncation
                      if (elCategoryLabel.getBBox().width > maxWidthCategoryLabel) {
                        const original = label;
                        let lo = 0;
                        let hi = original.length;

                        while (lo < hi) {
                          const mid = Math.floor((lo + hi + 1) / 2);
                          elCategoryLabel.textContent = original.slice(0, mid) + "…";
                          if (elCategoryLabel.getBBox().width <= maxWidthCategoryLabel) {
                            lo = mid;
                          } else {
                            hi = mid - 1;
                          }
                        }

                        elCategoryLabel.textContent = lo > 0 ? original.slice(0, lo) + "…" : "…";
                      }

                      elPercentageLabel.setAttribute("x", String(x + elCategoryLabel.getBBox().width + gap));
                    }}
                  >
                    <text
                      x={x}
                      y={y - 4}
                      textAnchor="start"
                      dominantBaseline="text-after-edge"
                      className="fill-text-primary text-caption"
                    >
                      {label}
                    </text>
                    <text
                      y={y - 4}
                      textAnchor="start"
                      dominantBaseline="text-after-edge"
                      className="fill-text-secondary text-caption"
                    >
                      {`(${currPortionInPercentage}%)`}
                    </text>
                    <text y={y + height} textAnchor="end" className="fill-text-secondary text-overline">
                      {currValue}
                    </text>
                  </g>
                );
              }}
            />
          </Bar>
          <Tooltip
            cursor={{
              fill: "transparent",
            }}
            content={({ payload }) => (
              <ChartTooltip>
                <section className="grid grid-cols-2 gap-x-space-md gap-y-space-sm">
                  <Inline gap="sm" align="center" wrap={false}>
                    <span className="size-status-dot rounded-xs bg-background-brand-secondary-light" />
                    <span className="justify-self-start text-overline leading-0">
                      {t("component.chart.histogram.tooltip.count")}
                    </span>
                  </Inline>
                  <span className="justify-self-end text-overline-emphasis">{payload?.[0]?.payload.value ?? 0}</span>

                  <Inline gap="sm" align="center" wrap={false}>
                    <span className="size-status-dot rounded-xs bg-background-on-default" />
                    <span className="justify-self-start text-overline leading-0">
                      {t("component.chart.histogram.tooltip.total")}
                    </span>
                  </Inline>
                  <span className="justify-self-end text-overline-emphasis">{amountTotalDataPoints}</span>
                </section>
              </ChartTooltip>
            )}
          />
        </BarChart>
      )}
    </RuntimeHeight>
  );
}

function resolveDataPoint<TCategory extends string>(
  data: HistogramChartDataPoint<TCategory>[],
  category: unknown,
  value: unknown,
): HistogramChartDataPoint<TCategory> {
  const normalizedCategory = String(category) as TCategory;

  return (
    data.find((point) => point.category === normalizedCategory) ?? {
      category: normalizedCategory,
      value: typeof value === "number" ? value : 0,
    }
  );
}

function BarBackground({
  x,
  y,
  width,
  height,
  onClick,
  onMouseEnter,
  onMouseLeave,
  fill,
}: React.SVGProps<SVGRectElement>): React.ReactNode {
  return (
    <rect
      className="fill-background-secondary"
      rx={2}
      {...{ x, y, width, height, onClick, onMouseEnter, onMouseLeave, fill }}
    />
  );
}
